import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import type { ViewColumn } from "@/lib/boardView";

interface NumberCellProps {
  value: number | null;
  column: ViewColumn;
  rowH?: number;
  onClick?: () => void;
  onPatch?: (value: number | null) => void;
}

const MONO_FAMILY = "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace";

function parseDecimal(raw: string): { ok: true; value: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: true, value: null };
  const cleaned = trimmed.replace(/[^0-9,.\-+]/g, "").replace(",", ".");
  if (cleaned === "" || cleaned === "-" || cleaned === "+" || cleaned === ".") return { ok: false };
  const n = parseFloat(cleaned);
  if (Number.isNaN(n) || !Number.isFinite(n)) return { ok: false };
  return { ok: true, value: n };
}

export function NumberCell({ value, column, rowH = 44, onClick, onPatch }: NumberCellProps) {
  const editable = typeof onPatch === "function";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  let display = "—";
  if (value != null) {
    display =
      column.format === "currency"
        ? fmtCurrency(value, column.currency ?? "EUR")
        : fmtNumber(value);
  }
  const isZero = value === 0;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function beginEdit() {
    if (!editable) return;
    if (value == null) {
      setDraft("");
    } else {
      setDraft(String(value).replace(".", ","));
    }
    setEditing(true);
  }

  function commit() {
    const parsed = parseDecimal(draft);
    if (!parsed.ok) {
      setEditing(false);
      return;
    }
    const next = parsed.value;
    if (next !== value && onPatch) {
      onPatch(next);
    }
    setEditing(false);
  }

  function cancel() {
    setEditing(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Tab") {
      commit();
      return;
    }
  }

  if (editing && editable) {
    return (
      <CellWrap rowH={rowH} align="right" interactive={false} pad="0">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            background: "transparent",
            padding: "0 12px",
            textAlign: "right",
            fontFamily: MONO_FAMILY,
            fontSize: 13.5,
            color: "var(--ink-1)",
            fontFeatureSettings: '"tnum"',
            fontVariantNumeric: "tabular-nums",
            boxSizing: "border-box",
            margin: 0,
            appearance: "none",
            MozAppearance: "textfield" as const,
          }}
        />
      </CellWrap>
    );
  }

  const handleClick = editable
    ? () => {
        beginEdit();
        onClick?.();
      }
    : onClick;

  return (
    <CellWrap rowH={rowH} {...(handleClick ? { onClick: handleClick } : {})} align="right">
      <span
        style={{
          fontSize: 13,
          color: value == null || isZero ? "var(--ink-4)" : "var(--ink-1)",
          fontFamily: MONO_FAMILY,
          fontFeatureSettings: '"tnum"',
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {display}
      </span>
    </CellWrap>
  );
}
